import { ref } from '@nuxtjs/composition-api';
import { STEP_TYPE } from '~/constants/StepType';
import useSubscriptions from '~/composable/checkout/validations/useSubscriptions'
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import useCheckoutInfo from '~/composable/useCheckoutInfo';
import useTlmCart from '~/composable/useTlmCart';
import useCustomer from '~/composable/customer/useCustomer';
import useMitID from '~/composable/useMitID'
import { CUSTOMER_TYPE, CHECKOUT_FLOW } from '~/constants/CheckoutFlow'
import { runPrePaymentSignupValidate } from '~/composable/checkout/handler/orderValidateHelpers';

const appContext = ref(null)
export default function mobileBroadbandSubscriptionFlowHandler () {
  const validValidationPromise = () => {
    return new Promise((resolve) => {
      resolve({
        actionStatus: VALIDATION_STATUS.VALID,
        signupBlockingNotifications: [],
        hasSignupBlockingNotification: false
      });
    });
  }
  const initialize = (app) => {
    return new Promise(() => {
      appContext.value = app;
    });
  }

  const preValidate = () => {
    const { preValidateSubscriptions } = useSubscriptions(appContext.value)
    return preValidateSubscriptions();
  }

  const validate = (subscriptionSignUpDataNew) => {
    const { validateSubscriptions } = useSubscriptions(appContext.value)
    return validateSubscriptions(subscriptionSignUpDataNew);
  }

  const execute = () => {
    return validValidationPromise()
  }

  const finalize = () => {
    return validValidationPromise()
  }

  // eslint-disable-next-line complexity
  const finalizeStep = async (stepType, state, appContext) => {
    const customerInfo = state.customerInfo
    const numberInfo = state.numberInfo
    const { addAddressInformation } = useCustomer(appContext)
    const { getCartID, isProtectedAddress, isSubscriptionOnly } = useTlmCart(appContext)
    const { getMitIdToken } = useMitID(appContext)
    const {
      addPrepaidEmail,
      addNumberInfo,
      addPermissionInformation
    } = useCheckoutInfo(appContext)
    switch (stepType) {
      case STEP_TYPE.SUBSCRIPTION_TYPE:
        await addNumberInfo(numberInfo.value)
        break;
      case STEP_TYPE.CUSTOMER_INFO_CONFIRMATION:
        {
          const billingAddressDTO = customerInfo.customerDetails.value.addressDTO
          const { menu, privateInfo, businessInfo } = customerInfo || {}
          const pickup = menu?.value === CUSTOMER_TYPE.BUSINESS
            ? businessInfo?.pickupLocation
            : privateInfo?.pickupLocation

          const requiresDropPoint = !isSubscriptionOnly.value
          const hasPickupAddress = Boolean(
            pickup &&
            pickup.streetName &&
            pickup.streetNumber &&
            pickup.postalCode &&
            pickup.city
          )

          const shippingAddressDTO = (requiresDropPoint && hasPickupAddress)
            ? {
              // shipping is NOT a person, it's the pakkeshop
                firstName: pickup.name,
                lastName: '',
                company: '',
                streetName: pickup.streetName,
                houseNumber: pickup.streetNumber,
                postalCode: pickup.postalCode,
                postalDistrict: pickup.city,
                floor: '',
                side: '',
                dropPointName: pickup.name,
                dropPointId: pickup.id,
                dropPointDistance: pickup.distance
              }
            : billingAddressDTO

          const addressPayload = {
            useBillingAddressAsShipping: !(requiresDropPoint && hasPickupAddress),
            shippingAddress: shippingAddressDTO,
            billingAddress: billingAddressDTO,
            emailAddress: customerInfo.customerDetails.value.email,
            cartID: getCartID.value
          }

          if (isProtectedAddress.value) {
            addressPayload.tempAddressIdentifier = getMitIdToken()
          }

          await addAddressInformation(addressPayload)
        }
        await addPermissionInformation({
          permissionEmail: customerInfo.permissionsAccepted.value,
          permissionSms: customerInfo.permissionsAccepted.value,
          permissionCall: customerInfo.permissionsAccepted.value,
          permissionTermsText: customerInfo.permissionText1 + customerInfo.permissionText2 + customerInfo.linkText1
        })
        await addPrepaidEmail(customerInfo.customerDetails.value.email)
        break;
      case STEP_TYPE.CONFIRMATION: {
        const validated = await runPrePaymentSignupValidate(
          state,
          appContext,
          CHECKOUT_FLOW.WITH_MOBILE_BROADBAND_SUBSCRIPTION
        )
        if (!validated) {
          return false
        }
        return true
      }
      default:
        return true;
    }
  }

  return {
    initialize,
    preValidate,
    validate,
    execute,
    finalize,
    finalizeStep
  }
}
